import React, { useContext, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { Pagination } from "@components";
import { AuthContext, useSiteSelection } from "@context";
import { useSiteList } from "@hooks";
import { ROLES } from "@constants/roles";
import "./dash.css";

const DashSiteList = () => {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const { compCode, compName, clearSite, setSiteSelection } = useSiteSelection();

    const isAdminOrR1 = auth?.role === ROLES.SUPER_ADMIN || auth?.roleId === 'R1';

    // 마운트 시 사업장 선택 초기화 — 사용자가 직접 사업장 행을 클릭해야 진행 가능 (Issue 1)
    useEffect(() => {
        clearSite();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // R2/R3: 컨텍스트에 회사코드가 없으면 auth에서 자동 설정
    useEffect(() => {
        if (!isAdminOrR1 && !compCode && auth?.compCode) {
            setSiteSelection(auth.compCode, auth.compName || "", null, null);
        }
    }, [isAdminOrR1, compCode, auth?.compCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const effectiveCompCode = compCode || (!isAdminOrR1 ? auth?.compCode || "" : "");
    const effectiveCompName = compName || (!isAdminOrR1 ? auth?.compName || "" : "");

    const {
        methods,
        data,
        isLoading,
        isError,
        error,
        onSubmit,
        handlePageChange,
        pageIndex,
        recordCountPerPage,
    } = useSiteList(effectiveCompCode);

    // R0/R1인데 회사 미선택 → 회사 선택으로 리다이렉트
    if (isAdminOrR1 && !effectiveCompCode) {
        return <Navigate to="/dash/company" replace />;
    }

    const handleSiteClick = (site) => {
        setSiteSelection(effectiveCompCode, effectiveCompName, site.siteCode, site.siteName);
        navigate("/dash/current");
    };

    if (isLoading) return <div className="contents-wrap" style={{ padding: "40px", textAlign: "center" }}>로딩 중...</div>;
    if (isError) return <div className="contents-wrap" style={{ padding: "40px" }}>오류가 발생했습니다: {error?.message || "알 수 없는 에러"}</div>;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="contents-wrap">
                    <div className="page-title-wrap">
                        <h2>
                            <span className="icon-dot-circle"></span>
                            대시보드 - 사업장 선택
                        </h2>
                        <div className="dash-breadcrumb" style={{ marginTop: "6px" }}>
                            <span className="dash-breadcrumb-comp">{effectiveCompName}</span>
                            <span className="dash-breadcrumb-sep"> &gt; </span>
                            <span>사업장 선택</span>
                        </div>
                    </div>

                    {/* R0/R1만 뒤로 가기 버튼 표시 */}
                    {isAdminOrR1 && (
                        <div style={{ marginBottom: "14px" }}>
                            <button
                                type="button"
                                onClick={() => navigate("/dash/company")}
                                style={{
                                    background: "none",
                                    border: "1px solid #ccc",
                                    padding: "6px 14px",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    color: "#555",
                                }}
                            >
                                ← 회사 선택으로 돌아가기
                            </button>
                        </div>
                    )}

                    <section>
                        <div className="section-tit-wrap">
                            <h3 className="border-left-primary">
                                &nbsp;사업장 목록
                                <span className="total-txt">
                                    {" "}| 총 <span className="red">{data?.totalCount || 0}</span> 건
                                </span>
                            </h3>
                        </div>

                        <div className="table-container overflow-x-auto table-list-wrapper">
                            <table className="table-list" style={{ width: "100%" }}>
                                <colgroup>
                                    <col style={{ width: "5%"  }} />
                                    <col style={{ width: "22%" }} />
                                    <col style={{ width: "13%" }} />
                                    <col style={{ width: "13%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "37%" }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>번호</th>
                                        <th>사업장명</th>
                                        <th>사업자등록번호</th>
                                        <th>전화번호</th>
                                        <th>대표자명</th>
                                        <th>주소</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data?.resultList && data.resultList.length > 0 ? (
                                        data.resultList.map((item, idx) => (
                                            <tr
                                                key={item.siteCode}
                                                onClick={() => handleSiteClick(item)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <td>{(pageIndex - 1) * recordCountPerPage + idx + 1}</td>
                                                <td style={{ color: "#1a73e8", fontWeight: "500" }}>{item.siteName}</td>
                                                <td>{item.bizNo}</td>
                                                <td>{item.phone}</td>
                                                <td>{item.repName}</td>
                                                <td>{item.addr1} {item.addr2 || ""}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="table-empty" style={{ textAlign: "center" }}>
                                                조회 결과가 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
                            <Pagination
                                totalPages={Math.ceil(
                                    data.paginationInfo.totalRecordCount /
                                    data.paginationInfo.recordCountPerPage
                                )}
                                currentPage={pageIndex}
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={10}
                            />
                        )}
                    </section>
                </div>
            </form>
        </FormProvider>
    );
};

export { DashSiteList };
